import { useState, type ReactNode } from "react";
import { ReportBarButton } from "./ReportButton";
import { api } from "../api";
import { needsYou, useChannelCollections, useCurrentChannel } from "../collections";
import { readFold, writeFold } from "../fold";
import { channelSettingsPath, Link, navArea, navigate, useLocation } from "../router";
import { dismissToast, set, useStore } from "../store";
import { fmtCredits, Icon } from "./bits";
import { DirectorBar } from "./Director";
import { DirectorDock } from "./Talk";
import { Logo } from "../brand/Logo";
import { InstallHint, UpdatePrompt } from "../pwa/PwaPrompts";
import { SafetyBadge, useSafetyCount } from "./SafetyBadge";
import { LowBalancePill } from "./PacksSheet"; // Wave 3 billing

// v4 (V4-H2, decision Q2): "Brand" became "Channel" (its settings; the colours and logo stay a "Brand" section inside)
// `help`: the data-help id how_do_i flashes (v4 D7, docs/product/help)
type NavItem = { to: string; label: string; icon: string; area: ReturnType<typeof navArea>; help: string };
const navFor = (channelId: string | null | undefined): NavItem[] => [
  { to: "/", label: "Home", icon: "home", area: "home", help: "nav.home" },
  { to: "/library", label: "Library", icon: "library", area: "library", help: "nav.library" },
  { to: channelSettingsPath(channelId), label: "Channel", icon: "brand", area: "channel", help: "nav.channel" },
  { to: "/credits", label: "Credits", icon: "credits", area: "credits", help: "nav.credits" },
];
// the rail also has room for these; on phones Settings is the gear in the top bar (People and Invites live inside Settings)
const RAIL_MORE = [
  { to: "/models", label: "Models", icon: "stack", admin: false, help: "nav.models" },
  { to: "/settings", label: "Settings", icon: "gear", admin: false, help: "nav.settings" },
  { to: "/admin/people", label: "People", icon: "person", admin: true, help: "nav.people" },
  { to: "/admin/invites", label: "Invites", icon: "ticket", admin: true, help: "nav.invites" },
  { to: "/admin/costs", label: "Costs", icon: "sliders", admin: true, help: "nav.costs" },
  { to: "/admin/services", label: "Services", icon: "bolt", admin: true, help: "nav.services" }, // 2026-11-03
  { to: "/admin/feedback", label: "Feedback", icon: "chat", admin: true, help: "nav.feedback" },
  { to: "/admin/requests", label: "Requests", icon: "check", admin: true, help: "nav.requests" }, // 2026-11-05: the feedback pipeline
  { to: "/admin/features", label: "Features", icon: "sliders", admin: true, help: "nav.features" }, // 2026-11-05: feature flags
  { to: "/admin/reports", label: "Reports", icon: "alert", admin: true, help: "nav.reports" }, // v4 (V4-SH)
  { to: "/admin/safety", label: "Safety", icon: "shield", admin: true, help: "nav.safety" }, // Wave 1 safety (count badge)
  { to: "/admin/showcase", label: "Showcase", icon: "film", admin: true, help: "nav.showcase" }, // MS-UI
];

/**
 * The brand in the app's chrome (web/src/brand/Logo.tsx; rules in docs/brand/README.md). The desktop rail and the
 * sign-in panel have room for the horizontal lockup (46 px, the chair's height); the phone top bar shows the chair
 * alone, since the name is on every screen around it.
 */
export function Wordmark({ small = false, named = false }: { small?: boolean; named?: boolean }) {
  const label = named ? { title: "CAO Studio" } : { decorative: true };
  return (
    <span className={`wordmark${small ? " wordmark-small" : ""}`}>
      {small ? <Logo layout="mark" height={32} {...label} /> : <Logo layout="horizontal" height={46} {...label} />}
    </span>
  );
}

function CreditChip() {
  const balance = useStore((s) => s.balance);
  return (
    <Link to="/credits" className="credit-chip" aria-label={`${fmtCredits(balance)} credits. Buy more`}>
      <Icon name="credits" size={16} />
      <span>{fmtCredits(balance)}</span>
    </Link>
  );
}

/** `home`: the Director is the page itself. `suite`: the editing suite shows its own Director panel. */
export function Shell({ children, home = false, suite = false }: { children: ReactNode; home?: boolean; suite?: boolean }) {
  const { path } = useLocation();
  const me = useStore((s) => s.me);
  const channel = useCurrentChannel();
  const NAV = navFor(channel?.id);
  const area = navArea(path);
  const active = (n: { to: string; area?: NavItem["area"] }) => (n.area ? n.area === area : path.startsWith(n.to));
  const safety = useSafetyCount(me?.user.role === "admin", path.startsWith("/admin")); // Wave 1 safety
  return (
    <div className={`shell${home ? " shell-home" : ""}${suite ? " shell-suite" : ""}`}>
      <nav className="rail" aria-label="Main">
        <Link to="/" className="rail-brand" aria-label="CAO Studio home">
          <Wordmark />
        </Link>
        <ul>
          {[...NAV, ...RAIL_MORE.filter((n) => !n.admin || me?.user.role === "admin")].map((n) => (
            <li key={n.label}>
              <Link to={n.to} className={`rail-link${active(n) ? " is-active" : ""}`} aria-current={active(n) ? "page" : undefined} data-help={n.help}>
                <Icon name={n.icon} />
                {n.label}
                {n.to === "/admin/safety" && <SafetyBadge count={safety} />}
              </Link>
              {n.label === "Home" && channel && <RailTree channelId={channel.id} path={path} />}
            </li>
          ))}
        </ul>
        <div className="rail-foot">
          {!home && !suite && <DirectorDock variant="rail" />}
          <CreditChip />
          {me && (
            <div className="rail-user">
              <span className="avatar" aria-hidden>
                {me.user.name.slice(0, 1).toUpperCase()}
              </span>
              <span className="rail-name">{me.user.name}</span>
              <button
                data-action="account.sign-out"
                type="button"
                className="icon-btn"
                aria-label="Sign out"
                title="Sign out"
                onClick={async () => {
                  await api.logout().catch(() => undefined);
                  set({ auth: "out", me: null });
                  navigate("/login");
                }}
              >
                <Icon name="logout" size={18} />
              </button>
            </div>
          )}
        </div>
      </nav>

      <header className="topbar">
        <Link to="/" aria-label="CAO Studio home">
          <Wordmark small />
        </Link>
        <span className="topbar-right">
          <CreditChip />
          <ReportBarButton />
          <Link to="/settings" data-help="nav.settings" className={`icon-btn topbar-gear${path.startsWith("/settings") || path.startsWith("/models") || path.startsWith("/admin") ? " is-active" : ""}`} aria-label="Settings">
            <Icon name="gear" size={20} />
          </Link>
        </span>
      </header>
      {/* UI audit 2026-11-05: only on Home, so a video or the Library opens on its content */}
      {path === "/" && <InstallHint />}

      <main className="main" id="main">
        {!path.startsWith("/credits") && <LowBalancePill />}
        {children}
      </main>

      {!home && (
        <div className={`dbar-slot${suite ? " dbar-slot-suite" : ""}${/^\/p\/[^/]+\/review$/.test(path) ? " dbar-slot-pill" : ""}`}>
          <DirectorBar />
        </div>
      )}

      <nav className="tabs" aria-label="Main">
        {NAV.map((n) => (
          <Link key={n.label} to={n.to} className={`tab${active(n) ? " is-active" : ""}`} aria-current={active(n) ? "page" : undefined} data-help={n.help}>
            <Icon name={n.icon} size={22} />
            <span>{n.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}

/** v4 (V4-H2, spec-v4 5.1): the current channel's collections under Home on the rail, folded away with a chevron. */
function RailTree({ channelId, path }: { channelId: string; path: string }) {
  const cols = useChannelCollections(channelId);
  const [open, setOpen] = useState(() => readFold("rail.tree", true));
  if (!cols?.length) return null;
  const MAX = 7;
  return (
    <div className={`rail-tree${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="rail-tree-toggle"
        aria-expanded={open}
        aria-label={open ? "Hide collections" : `Show collections (${cols.length})`}
        onClick={() => {
          setOpen(!open);
          writeFold("rail.tree", !open);
        }}
      >
        <Icon name="chevron" size={14} />
        <span>Collections</span>
        {!open && <span className="rail-tree-n">{cols.length}</span>}
      </button>
      {open && (
        <ul className="rail-tree-list">
          {cols.slice(0, MAX).map((c) => {
            const here = path === `/k/${c.id}` || path.startsWith(`/k/${c.id}/`);
            const need = needsYou(c.needs);
            return (
              <li key={c.id}>
                <Link to={`/k/${encodeURIComponent(c.id)}`} className={`rail-tree-link${here ? " is-active" : ""}`} aria-current={here ? "page" : undefined}>
                  <span className="rail-tree-name">{c.name}</span>
                  {need > 0 && <span className="rail-tree-dot" aria-label={`${need} need you`} />}
                </Link>
              </li>
            );
          })}
          {cols.length > MAX && (
            <li>
              <Link to={`/c/${encodeURIComponent(channelId)}`} className="rail-tree-link rail-tree-more">
                All {cols.length}
              </Link>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export function Toasts() {
  const toasts = useStore((s) => s.toasts);
  return (
    <div className="toasts" aria-live="polite">
      <UpdatePrompt />
      {toasts.map((t) => (
        <p key={t.id} className={`toast toast-${t.tone}${t.action ? " has-action" : ""}`}>
          <span className="toast-text">{t.text}</span>
          {t.action && (
            <button
              type="button"
              className="toast-act"
              onClick={() => {
                dismissToast(t.id);
                t.action!.run();
              }}
            >
              {t.action.label}
            </button>
          )}
        </p>
      ))}
    </div>
  );
}


// the overlay moved to Stage.tsx (the v2.1 playback hand-off); re-exported so App.tsx keeps its import
export { PlayerOverlay } from "./Stage";
